import { Typography } from '@mui/material'
import React, { ChangeEvent, memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { UploadEIN } from 'src/constants/actionTypes'
import EINIcon from 'src/assets/icons/bgc.svg'
import { CollectDataStepIndex } from 'src/components/client/CollectDataStepIndex'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { Formik } from 'formik'
import { EINFormSchema } from './EINForm.validator'
import { FormikFileInput } from '../shared/form/formik/FormikFileInput'
import { EINFormValues } from './EINForm.interface'
import { FormErrorMessage } from '../shared/form/FormErrorMessage'
import { BasicRadioGroup } from '../shared/form/BasicRadioGroup'
import { FormikTextField } from '../shared/form/formik/FormikTextField'
import { FormikSelect } from '../shared/form/formik/FormikSelect'
import { states } from 'src/constants/states'
import { usePersona } from 'src/hooks/usePersona'
import { Button } from '../shared/Button'

const initialValues: EINFormValues = {
  file: undefined,
  ein: '',
  businessName: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
}

const showFormValues = [
  {
    label: 'Yes',
    value: 'yes',
  },
  {
    label: 'No',
    value: 'no',
  },
]

const EIN_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]

const EINFormComponent = () => {
  const dispatch = useDispatch()

  const currentStep = useSelector(getCurrentCollectDataStep)

  const { startPersona } = usePersona()

  const [showForm, setShowForm] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleFormSubmit = async (values: EINFormValues) => {
    if (!currentStep?.id) {
      return
    }

    setIsLoading(true)
    setError(null)

    const requestBody = showForm
      ? {
          ...values,
          stepId: currentStep.id,
          isBusinessEntityFlow: true,
        }
      : {
          stepId: currentStep.id,
          isBusinessEntityFlow: false,
        }

    try {
      await createAsyncAction(dispatch, UploadEIN.request(requestBody))

      if (showForm) {
        await startPersona(currentStep.id)
      }
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeShowForm = useCallback((e: ChangeEvent<{ value: string }>) => {
    setShowForm(e.target.value === 'yes')
  }, [])

  useEffect(() => {
    if (!currentStep?.optional) {
      setShowForm(true)
    }
  }, [currentStep?.optional])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={showForm ? EINFormSchema : undefined}
    >
      {({ handleSubmit }) => (
        <div css={tw`h-full w-full flex flex-col justify-between`}>
          <div>
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
            >
              <img src={EINIcon} />
            </div>
            <CollectDataStepIndex stepId={currentStep?.id} />
            <Typography css={tw`mb-6 mt-2`} variant="h2">
              Business Entity
            </Typography>
            <Typography css={tw`text-shades-400`}>
              Please enter your business information, if applicable.
            </Typography>
            {currentStep?.optional && (
              <div css={tw`mt-6 bg-shades-0 p-4 rounded-2xl mb-8`}>
                <Typography css={tw`mb-3 text-shades-400`} variant="h6">
                  Are you registering as a business entity? (This only applies to contractors who
                  have a registered business name and EIN, and wish to register with GigSafe as a
                  business. If this does not apply to you, please check 'No').
                </Typography>
                <BasicRadioGroup options={showFormValues} onChange={handleChangeShowForm} />
              </div>
            )}
            {showForm && (
              <div>
                <Typography variant="overline" css={tw`text-shades-400`}>
                  ENTER YOUR BUSINESS INFORMATION
                </Typography>
                <div css={tw`bg-shades-0 p-4 rounded-2xl mb-8 mt-2`}>
                  <FormikTextField
                    name="ein"
                    css={tw`w-full mb-6`}
                    label="EIN"
                    placeholder="Enter your EIN"
                    mask={EIN_MASK}
                  />
                  <FormikTextField
                    name="businessName"
                    css={tw`w-full`}
                    label="Business Name"
                    placeholder="Enter your business name"
                  />
                </div>
                <Typography variant="overline" css={tw`text-shades-400`}>
                  ENTER YOUR BUSINESS ADDRESS
                </Typography>
                <div css={tw`bg-shades-0 p-4 rounded-2xl mb-8 mt-2`}>
                  <FormikTextField
                    name="streetAddress"
                    css={tw`w-full mb-6`}
                    label="Street address"
                    placeholder="Enter street address"
                  />
                  <FormikTextField
                    name="city"
                    css={tw`w-full mb-6`}
                    label="City"
                    placeholder="Enter city"
                  />
                  <div css={tw`w-full mb-6`}>
                    <FormikSelect
                      name="state"
                      label="State"
                      placeholder="Select state"
                      options={states}
                    />
                  </div>
                  <FormikTextField
                    name="zipCode"
                    css={tw`w-full`}
                    label="Zip code"
                    placeholder="Enter zip code"
                  />
                </div>
                <Typography variant="overline" css={tw`text-shades-400`}>
                  UPLOAD EIN LETTER
                </Typography>
                <div css={tw`bg-shades-0 p-4 rounded-2xl mb-8 mt-2`}>
                  <FormikFileInput name="file" />
                </div>
              </div>
            )}
          </div>
          <FormErrorMessage css={tw`mb-4`} error={error} />
          <Button
            className={tw`w-full`}
            onClick={() => {
              handleSubmit()
            }}
            loading={isLoading}
            disabled={typeof showForm !== 'boolean'}
          >
            Submit document
          </Button>
        </div>
      )}
    </Formik>
  )
}

export const EINForm = memo(EINFormComponent)
